import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription } from '../ui/alert';
import { useToast } from '../ui/use-toast';
import { 
  Settings, 
  Users, 
  Clock, 
  Coffee,
  Save,
  AlertTriangle,
  CheckCircle,
  Search,
  Filter
} from 'lucide-react';
import { User } from '../../types';
import { apiService } from '../../services/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface BulkWorkSettingsProps {
  allUsers: User[];
  onRefresh: () => void;
}

export const BulkWorkSettings: React.FC<BulkWorkSettingsProps> = ({
  allUsers,
  onRefresh
}) => {
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [workHours, setWorkHours] = useState<string>('8');
  const [breakMinutes, setBreakMinutes] = useState<string>('60');
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [teamFilter, setTeamFilter] = useState<string>('all');
  const { toast } = useToast();

  // Filter workers only (not admins, supervisors, etc.)
  const workers = useMemo(() => {
    return allUsers.filter(user => 
      user.role === 'worker' || user.role === 'muncitor'
    );
  }, [allUsers]);

  // Apply filters
  const filteredWorkers = useMemo(() => {
    let filtered = workers;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(query)
      );
    }

    // Team filter
    if (teamFilter !== 'all') {
      filtered = filtered.filter(user =>
        user.teamId?.toString() === teamFilter
      );
    }

    return filtered;
  }, [workers, searchQuery, teamFilter]);

  // Get unique teams
  const teams = useMemo(() => {
    const uniqueTeams = new Map<number, string>();
    workers.forEach(worker => {
      if (worker.teamId && worker.team?.name) {
        uniqueTeams.set(worker.teamId, worker.team.name);
      }
    });
    return Array.from(uniqueTeams.entries());
  }, [workers]);

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredWorkers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredWorkers.map(w => w.id));
    }
  };

  const handleSelectUser = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleApplySettings = async () => {
    if (selectedUsers.length === 0) {
      toast({
        title: 'Atenție',
        description: 'Selectați cel puțin un muncitor',
        variant: 'destructive'
      });
      return;
    }

    const workMinutes = parseInt(workHours) * 60;
    const breakMin = parseInt(breakMinutes);

    if (isNaN(workMinutes) || workMinutes < 60 || workMinutes > 720) {
      toast({
        title: 'Eroare',
        description: 'Orele de lucru trebuie să fie între 1 și 12',
        variant: 'destructive'
      });
      return;
    }

    if (isNaN(breakMin) || breakMin < 0 || breakMin > 180) {
      toast({
        title: 'Eroare',
        description: 'Timpul de pauză trebuie să fie între 0 și 180 minute',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);

    try {
      // Update each selected user
      const updatePromises = selectedUsers.map(userId =>
        apiService.updateUserWorkSettings(userId, {
          dailyWorkQuotaMinutes: workMinutes,
          dailyBreakQuotaMinutes: breakMin
        })
      );

      await Promise.all(updatePromises);

      toast({
        title: 'Succes',
        description: `Setările au fost actualizate pentru ${selectedUsers.length} muncitori`,
      });

      setSelectedUsers([]);
      onRefresh();
    } catch (error) {
      console.error('Error updating work settings:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut actualiza setările. Încercați din nou.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Info Alert */}
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Configurați normele de lucru și pauză pentru mai mulți muncitori simultan. 
          Valorile implicite sunt 8 ore lucru și 1 oră pauză pe zi.
        </AlertDescription>
      </Alert>

      {/* Settings Input */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
        <div>
          <Label className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Ore de lucru pe zi
          </Label>
          <Input
            type="number"
            value={workHours}
            onChange={(e) => setWorkHours(e.target.value)}
            min="1"
            max="12"
            step="0.5"
            className="mt-1"
          />
          <p className="text-xs text-gray-600 mt-1">Standard: 8 ore</p>
        </div>

        <div>
          <Label className="flex items-center gap-2">
            <Coffee className="h-4 w-4" />
            Timp de pauză (minute)
          </Label>
          <Input
            type="number"
            value={breakMinutes}
            onChange={(e) => setBreakMinutes(e.target.value)}
            min="0"
            max="180"
            step="15"
            className="mt-1"
          />
          <p className="text-xs text-gray-600 mt-1">Standard: 60 minute</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-end">
        <div className="flex-1">
          <Label>Caută muncitor</Label>
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Caută după nume..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="w-48">
          <Label>Filtrează după echipă</Label>
          <Select value={teamFilter} onValueChange={setTeamFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Toate echipele" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate echipele</SelectItem>
              {teams.map(([teamId, teamName]) => (
                <SelectItem key={teamId} value={teamId.toString()}>
                  {teamName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Workers List */}
      <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
        <div className="flex items-center justify-between mb-3 pb-3 border-b">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={selectedUsers.length === filteredWorkers.length && filteredWorkers.length > 0}
              onCheckedChange={handleSelectAll}
            />
            <Label className="cursor-pointer">
              Selectează toți ({filteredWorkers.length})
            </Label>
          </div>
          {selectedUsers.length > 0 && (
            <Badge variant="secondary">
              {selectedUsers.length} selectați
            </Badge>
          )}
        </div>

        <div className="space-y-2">
          {filteredWorkers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p>Nu s-au găsit muncitori</p>
            </div>
          ) : (
            filteredWorkers.map(worker => (
              <div
                key={worker.id}
                className="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedUsers.includes(worker.id)}
                    onCheckedChange={() => handleSelectUser(worker.id)}
                  />
                  <div>
                    <p className="font-medium">{worker.name}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      {worker.team?.name && (
                        <Badge variant="outline" className="text-xs">
                          {worker.team.name}
                        </Badge>
                      )}
                      <span className="text-xs">
                        Normă: {(worker.dailyWorkQuotaMinutes || 480) / 60}h lucru, 
                        {' '}{worker.dailyBreakQuotaMinutes || 60} min pauză
                      </span>
                    </div>
                  </div>
                </div>
                
                {worker.dailyWorkQuotaMinutes && (
                  <div className="text-sm text-gray-500">
                    {worker.dailyWorkQuotaMinutes !== 480 || worker.dailyBreakQuotaMinutes !== 60 ? (
                      <Badge variant="secondary" className="text-xs">
                        Personalizat
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        Standard
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600">
          {selectedUsers.length > 0 
            ? `Se vor actualiza setările pentru ${selectedUsers.length} muncitori`
            : 'Selectați muncitorii pentru a aplica setările'}
        </p>

        <Button
          onClick={handleApplySettings}
          disabled={selectedUsers.length === 0 || isLoading}
          className="flex items-center gap-2"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Se aplică...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Aplică Setările
            </>
          )}
        </Button>
      </div>
    </div>
  );
};